import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';

export default function FunctionalAdvancedLabTesting() {
  const { isLoaded, user } = useUser();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Functional and Advanced Lab Testing for Longevity and Health Optimization",
      subtitle: "A Clinician's Introduction to Proactive, Personalized Medicine",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="flex justify-center mb-8">
              <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .label { font-family: Arial, sans-serif; font-size: 14px; fill: #333; }
                  .title { font-family: Arial, sans-serif; font-size: 18px; font-weight: bold; fill: #2563eb; }
                  .molecule { fill: #3b82f6; stroke: #1e40af; stroke-width: 2; }
                  .dna { fill: #ef4444; stroke: #dc2626; }
                  .cell { fill: #10b981; stroke: #059669; }
                `}</style>
                
                <circle cx="100" cy="100" r="30" className="molecule"/>
                <text x="100" y="107" className="label" textAnchor="middle" fill="white">Biomarkers</text>
                
                <rect x="170" y="70" width="60" height="60" className="cell" rx="10"/>
                <text x="200" y="107" className="label" textAnchor="middle" fill="white">Function</text>
                
                <polygon points="270,70 320,85 320,115 270,130 270,70" className="dna"/>
                <text x="295" y="107" className="label" textAnchor="middle" fill="white">Health</text>
                
                <text x="200" y="40" className="title" textAnchor="middle">Proactive Medicine</text>
                <text x="200" y="170" className="label" textAnchor="middle">From Reactive to Predictive Healthcare</text>
              </svg>
            </div>
            
            <div className="space-y-4 text-center">
              <p className="text-lg text-gray-700">
                Transforming clinical practice through advanced functional testing and personalized health optimization strategies
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  Moving beyond disease detection to health optimization and longevity enhancement
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Paradigm Shift: From Reactive to Proactive Medicine",
      subtitle: "Evolving Medical Practice: Optimizing Healthspan",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Traditional Model</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>• Primarily reactive, disease-focused</p>
                  <p>• Intervention often occurs after illness manifests</p>
                  <p>• Wait for symptoms to develop</p>
                  <p>• Standard reference ranges based on sick populations</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Longevity Medicine Model</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Proactive, centered on optimizing healthspan</p>
                  <p>• Preventing age-related decline</p>
                  <p>• Enhancing vitality and function</p>
                  <p>• Understanding molecular processes before disease</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Paradigm Shift</h4>
              <div className="flex justify-center">
                <svg width="350" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 11px; fill: #333; }
                    .reactive { fill: #fca5a5; stroke: #dc2626; }
                    .proactive { fill: #86efac; stroke: #16a34a; }
                    .arrow { stroke: #374151; stroke-width: 3; }
                  `}</style>
                  
                  <circle cx="100" cy="100" r="60" className="reactive"/>
                  <text x="100" y="90" className="label" textAnchor="middle" fontWeight="bold">Reactive</text>
                  <text x="100" y="105" className="label" textAnchor="middle">Medicine</text>
                  <text x="100" y="120" className="label" textAnchor="middle">(Disease Focus)</text>
                  
                  <path d="M170,100 L180,100" className="arrow" markerEnd="url(#arrowhead)"/>
                  
                  <circle cx="250" cy="100" r="60" className="proactive"/>
                  <text x="250" y="85" className="label" textAnchor="middle" fontWeight="bold">Proactive</text>
                  <text x="250" y="100" className="label" textAnchor="middle">Medicine</text>
                  <text x="250" y="115" className="label" textAnchor="middle">(Health</text>
                  <text x="250" y="130" className="label" textAnchor="middle">Optimization)</text>
                  
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#374151"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-900 mb-3">Core Principle</h4>
            <p className="text-sm text-blue-800">
              Understanding and modulating intricate molecular and cellular processes before overt disease manifests.
              The goal is not just adding years to life, but life to years.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Why Functional & Advanced Lab Testing in Longevity?",
      subtitle: "Beyond Standard Labs: A Deeper Physiological Insight",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Conventional Labs</h3>
                <p className="text-sm text-orange-800">
                  Primarily designed to identify established disease and pathology
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Functional/Advanced Testing</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Provides dynamic window into complex systems</p>
                  <p>• Uncovers root causes and subtle imbalances</p>
                  <p>• Focuses on early alterations before escalation</p>
                  <p>• Enables timely, personalized interventions</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Testing Paradigms</h4>
              <div className="flex justify-center">
                <svg width="400" height="180" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                    .conventional { fill: #bfdbfe; stroke: #3b82f6; stroke-width: 2; }
                    .functional { fill: #bbf7d0; stroke: #10b981; stroke-width: 2; }
                    .overlap { fill: #a7f3d0; opacity: 0.7; }
                  `}</style>
                  
                  <rect x="10" y="10" width="380" height="160" fill="#f8fafc" stroke="#e2e8f0" rx="10"/>
                  
                  <circle cx="100" cy="90" r="60" className="conventional"/>
                  <text x="100" y="85" className="label" textAnchor="middle" fontWeight="bold">Conventional</text>
                  <text x="100" y="105" className="label" textAnchor="middle">Testing</text>
                  <text x="100" y="125" className="label" textAnchor="middle" fontSize="10">(Disease ID)</text>
                  
                  <circle cx="260" cy="90" r="75" className="functional"/>
                  <text x="260" y="70" className="label" textAnchor="middle" fontWeight="bold">Functional/</text>
                  <text x="260" y="90" className="label" textAnchor="middle" fontWeight="bold">Advanced</text>
                  <text x="260" y="110" className="label" textAnchor="middle" fontWeight="bold">Testing</text>
                  <text x="260" y="130" className="label" textAnchor="middle" fontSize="10">(Optimal Function,</text>
                  <text x="260" y="145" className="label" textAnchor="middle" fontSize="10">Root Causes, Early Imbalances)</text>
                  
                  <ellipse cx="180" cy="90" rx="20" ry="30" className="overlap"/>
                  <text x="180" y="95" className="label" textAnchor="middle" fontSize="11" fontWeight="bold">Early Risk</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Key Pillars of Longevity: What We Assess & Modulate",
      subtitle: "Targeting the Drivers of Aging for Clinical Intervention",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <p className="text-sm text-blue-800 mb-4">
              Aging is driven by interconnected biological mechanisms ("Hallmarks of Aging"). 
              This presentation focuses on clinically actionable pillars assessed via advanced testing:
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-semibold text-red-900 mb-2">1. Redox Balance & Oxidative Stress</h4>
                <p className="text-sm text-red-800">Cellular damage control</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                <h4 className="font-semibold text-purple-900 mb-2">2. Autophagy & Cellular Senescence</h4>
                <p className="text-sm text-purple-800">Cellular renewal and waste management</p>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                <h4 className="font-semibold text-orange-900 mb-2">3. Immunosenescence & "Inflammaging"</h4>
                <p className="text-sm text-orange-800">Maintaining immune vigilance</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-green-900 mb-2">4. Mitochondrial Function & Biogenesis</h4>
                <p className="text-sm text-green-800">Cellular energy and vitality</p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">5. Key Signaling Pathways</h4>
                <p className="text-sm text-blue-800">Orchestrators of cellular fate (IIS, mTOR, AMPK, Sirtuins)</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Additional Considerations</h4>
                <p className="text-sm text-gray-800">Hormonal Balance, Detoxification, Membrane Health, Neurotrophic Support</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pillar 1: Oxidative Stress & Redox Balance",
      subtitle: "The Double-Edged Sword of Reactive Species",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Oxidative Stress</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>Imbalance favoring pro-oxidants (ROS, RNS) over antioxidant defenses</p>
                  <p>Leads to cumulative damage to lipids, proteins, DNA</p>
                  <p>Accelerates aging and chronic disease</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Hormesis Concept</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>Mild, controlled ROS can induce protective antioxidant responses</p>
                  <p>"Free Radical Theory of Longevity"</p>
                  <p><strong>Goal:</strong> Maintain healthy redox balance, not complete ROS eradication</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Redox Balance Scale</h4>
              <div className="flex justify-center">
                <svg width="300" height="150" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 11px; fill: #333; }
                    .antioxidant { fill: #86efac; stroke: #16a34a; }
                    .prooxidant { fill: #fca5a5; stroke: #dc2626; }
                    .fulcrum { fill: #6b7280; }
                  `}</style>
                  
                  <rect x="5" y="5" width="290" height="140" fill="#fffbeb" stroke="#e2e8f0" rx="10"/>
                  
                  {/* Fulcrum */}
                  <polygon points="145,120 155,120 150,100" className="fulcrum"/>
                  
                  {/* Beam (tilted for Oxidative Stress) */}
                  <line x1="50" y1="80" x2="250" y2="40" stroke="#374151" strokeWidth="6"/>
                  
                  {/* Left side (lower) - Antioxidants */}
                  <rect x="30" y="80" width="80" height="35" className="antioxidant" rx="5"/>
                  <text x="70" y="95" className="label" textAnchor="middle">Antioxidant</text>
                  <text x="70" y="108" className="label" textAnchor="middle">Defenses</text>
                  
                  {/* Right side (higher) - Pro-oxidants */}
                  <rect x="190" y="5" width="80" height="35" className="prooxidant" rx="5"/>
                  <text x="230" y="18" className="label" textAnchor="middle">Pro-oxidants</text>
                  <text x="230" y="31" className="label" textAnchor="middle">(ROS, RNS)</text>
                  
                  <text x="150" y="135" className="label" textAnchor="middle" fontWeight="bold" fill="#dc2626">Oxidative Stress</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Oxidative Stress: Key Biomarkers & Clinical Interpretation",
      subtitle: "Quantifying Oxidative Burden and Antioxidant Capacity",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Clinically Relevant Biomarkers</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="border border-gray-300 p-2 text-left">Biomarker</th>
                    <th className="border border-gray-300 p-2 text-left">Sample</th>
                    <th className="border border-gray-300 p-2 text-left">Dysfunction</th>
                    <th className="border border-gray-300 p-2 text-left">Clinical Relevance</th>
                    <th className="border border-gray-300 p-2 text-left">Key Refs</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-red-50">
                    <td className="border border-gray-300 p-2 font-semibold" colSpan={5}>Lipid Peroxidation</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-2">F2-Isoprostanes (8-iso-PGF2α/Creat)</td>
                    <td className="border border-gray-300 p-2">Urine</td>
                    <td className="border border-gray-300 p-2">↑</td>
                    <td className="border border-gray-300 p-2">"Gold standard" for in vivo lipid damage; CVD risk</td>
                    <td className="border border-gray-300 p-2">8, 10</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-2">MDA / TBARS</td>
                    <td className="border border-gray-300 p-2">Serum/Plasma</td>
                    <td className="border border-gray-300 p-2">↑</td>
                    <td className="border border-gray-300 p-2">General lipid peroxidation; less specific</td>
                    <td className="border border-gray-300 p-2">9</td>
                  </tr>
                  <tr className="bg-purple-50">
                    <td className="border border-gray-300 p-2 font-semibold" colSpan={5}>DNA Damage</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-2">8-OHdG</td>
                    <td className="border border-gray-300 p-2">Urine/Serum</td>
                    <td className="border border-gray-300 p-2">↑</td>
                    <td className="border border-gray-300 p-2">Oxidative DNA lesions; aging, cancer risk</td>
                    <td className="border border-gray-300 p-2">9</td>
                  </tr>
                  <tr className="bg-orange-50">
                    <td className="border border-gray-300 p-2 font-semibold" colSpan={5}>Protein Damage</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-2">AGEs (serum, skin SAF)</td>
                    <td className="border border-gray-300 p-2">Serum/Skin</td>
                    <td className="border border-gray-300 p-2">↑</td>
                    <td className="border border-gray-300 p-2">Protein glycation, tissue stiffness, CVD, diabetes</td>
                    <td className="border border-gray-300 p-2">11, 12</td>
                  </tr>
                  <tr className="bg-green-50">
                    <td className="border border-gray-300 p-2 font-semibold" colSpan={5}>Antioxidant Status</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-2">Glutathione (GSH/GSSG Ratio)</td>
                    <td className="border border-gray-300 p-2">Whole Blood</td>
                    <td className="border border-gray-300 p-2">↓ Ratio, ↓ GSH</td>
                    <td className="border border-gray-300 p-2">Key intracellular antioxidant; impaired detox/defense</td>
                    <td className="border border-gray-300 p-2">15, 17, 18</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-2">Coenzyme Q10 (Total, Ubiquinol)</td>
                    <td className="border border-gray-300 p-2">Plasma/Serum</td>
                    <td className="border border-gray-300 p-2">↓</td>
                    <td className="border border-gray-300 p-2">Mitochondrial function, antioxidant; statin depletion</td>
                    <td className="border border-gray-300 p-2">24, 25, 26, 27</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Clinical Interpretation</h4>
            <p className="text-sm text-blue-800">
              Consider patterns, not just isolated markers. Relate to symptoms and lifestyle factors for comprehensive assessment.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Interventions for Redox Balance & Study Critiques",
      subtitle: "Restoring Balance: Targeted Strategies",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Dietary Approaches</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Colorful fruits/vegetables (antioxidants, Nrf2 activators like sulforaphane)</p>
                  <p>• Reduce pro-oxidant foods (processed, sugars)</p>
                  <p>• Mediterranean diet patterns</p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Targeted Supplementation</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>N-Acetylcysteine (NAC):</strong> GSH precursor. Dose: 600-1800 mg/day</p>
                  <p><strong>Alpha-Lipoic Acid (ALA):</strong> "Universal antioxidant." Dose: 200-600 mg/day</p>
                  <p><strong>Coenzyme Q10 (Ubiquinol):</strong> Dose: 100-200 mg/day</p>
                  <p><strong>Vitamin C:</strong> Dose: 500-1000 mg/day</p>
                  <p><strong>Selenium:</strong> GPx cofactor. Dose: 50-200 mcg/day</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Study Critiques</h3>
                <div className="space-y-2 text-sm text-yellow-800">
                  <p><strong>Landmark Studies (PHS II, SELECT):</strong></p>
                  <p>• Often null or mixed results for broad antioxidant supplementation</p>
                  <p>• SELECT showed potential harm (↑ prostate cancer risk with Vit E)</p>
                </div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Key Lessons</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>• "More is not always better"</p>
                  <p>• Need for personalized, data-driven approaches</p>
                  <p>• Consider specific deficiencies and oxidative stress levels</p>
                  <p>• Indiscriminate use may blunt beneficial hormetic signaling</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pillar 2: Autophagy & Cellular Senescence",
      subtitle: "Cellular Housekeeping vs. \"Zombie\" Cells",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Autophagy ("Self-Eating")</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Essential catabolic process</p>
                  <p>• Degrades/recycles damaged organelles & proteins</p>
                  <p>• Maintains cellular quality control</p>
                  <p>• Declines with age, contributing to loss of proteostasis</p>
                </div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Cellular Senescence</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>• Stable cell cycle arrest</p>
                  <p>• Triggered by DNA damage, telomere attrition, oxidative stress</p>
                  <p>• Accumulate with age, adopt pro-inflammatory SASP</p>
                  <p>• SASP drives "inflammaging" and tissue dysfunction</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Cellular Fate Pathways</h4>
              <div className="flex justify-center">
                <svg width="400" height="220" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                    .healthy { fill: #bfdbfe; stroke: #3b82f6; stroke-width: 2; }
                    .senescent { fill: #fca5a5; stroke: #dc2626; stroke-width: 2; }
                    .autophagy { stroke: #10b981; stroke-width: 2.5; fill: none; }
                    .sasp { stroke: #b91c1c; stroke-width: 1.5; fill: none; }
                  `}</style>
                  
                  <rect x="5" y="5" width="390" height="210" fill="#f8fafc" stroke="#e2e8f0" rx="10"/>
                  
                  {/* Healthy Cell with Autophagy */}
                  <g transform="translate(30, 30)">
                    <circle cx="60" cy="60" r="50" className="healthy"/>
                    <text x="60" y="125" className="label" textAnchor="middle">Healthy Cell</text>
                    
                    {/* Autophagy symbol */}
                    <path d="M 45 50 A 15 15 0 1 1 75 50 A 15 15 0 1 1 45 50 M 50 45 L 40 55 M 70 45 L 80 55 M 60 75 L 55 85 M 60 75 L 65 85" 
                          className="autophagy" transform="rotate(15, 60, 60)"/>
                    <text x="60" y="65" className="label" fontWeight="bold" textAnchor="middle" fill="#059669">Autophagy</text>
                  </g>
                  
                  {/* Arrow */}
                  <line x1="150" y1="80" x2="200" y2="80" stroke="#374151" strokeWidth="2.5" markerEnd="url(#arrowhead)"/>
                  <text x="175" y="70" className="label" textAnchor="middle">Aging/</text>
                  <text x="175" y="90" className="label" textAnchor="middle">Stress</text>
                  
                  {/* Senescent Cell */}
                  <g transform="translate(220, 20)">
                    <ellipse cx="80" cy="70" rx="70" ry="55" className="senescent"/>
                    <text x="80" y="145" className="label" textAnchor="middle">Senescent Cell</text>
                    <text x="80" y="75" className="label" fontWeight="bold" textAnchor="middle" fill="#991b1b">SASP Release</text>
                    <text x="80" y="165" className="label" textAnchor="middle" fill="#dc2626">(Inflammaging)</text>
                    
                    {/* SASP signals */}
                    <path d="M 80 15 Q 85 5, 95 10" className="sasp"/>
                    <path d="M 130 50 Q 145 45, 150 55" className="sasp"/>
                    <path d="M 120 100 Q 135 105, 130 115" className="sasp"/>
                    <path d="M 30 50 Q 15 45, 10 55" className="sasp"/>
                  </g>
                  
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#374151"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Autophagy/Senescence: Biomarkers & Interventions",
      subtitle: "Modulating Cellular Renewal and \"Zombie\" Cell Burden",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Senescence Biomarkers</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>p16<sup>INK4a</sup> (CDKN2A):</strong> ↑ with age/senescence (mRNA in T-lymphocytes)</p>
                  <p><strong>SASP Factors:</strong> IL-6, TNF-α, IL-8, MMPs (Serum/Plasma)</p>
                  <p>Correlates with physiological decline</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Autophagy Induction</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Lifestyle:</strong> Caloric Restriction (CR), Intermittent Fasting (IF)</p>
                  <p><strong>Rapamycin:</strong> 5-10 mg weekly (mTOR inhibitor)</p>
                  <p><strong>Others:</strong> Metformin, Spermidine, Resveratrol</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Senolytics (Pulsed Dosing)</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p><strong>Dasatinib + Quercetin (D+Q):</strong></p>
                  <p>D 100mg + Q 1000-1250mg, 3 consecutive days monthly</p>
                  <p><strong>Fisetin:</strong> ~20mg/kg for 2 days/month</p>
                  <p>Showed ↓ senescent cells in human studies</p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Clinical Pearl</h3>
                <p className="text-sm text-blue-800">
                  Senescent cells have beneficial roles (e.g., wound healing). 
                  Intermittent/pulsed senolytic therapy may be preferable to continuous ablation.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pillar 3: Immunosenescence & \"Inflammaging\"",
      subtitle: "Rejuvenating the Aging Immune System",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Immunosenescence</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p>• Age-associated decline in immune function</p>
                  <p>• Reduced response to new antigens</p>
                  <p>• Impaired tumor surveillance</p>
                  <p>• Thymic involution → ↓ Naïve T cells</p>
                </div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">"Inflammaging"</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>• Chronic, low-grade systemic inflammation</p>
                  <p>• Driven by senescent cells (SASP)</p>
                  <p>• Gut dysbiosis, chronic infections</p>
                  <p>• Altered CD4/CD8 ratio (often ↓ less than 1.0)</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Key Features</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• Accumulation of senescent/exhausted T cells</p>
                  <p>• CD28-, CD57+, KLRG1+ markers</p>
                  <p>• ↓ NK cell cytotoxic function</p>
                  <p>• ↑ Susceptibility to infections, cancer</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Consequences</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Increased infection susceptibility</p>
                  <p>• Reduced vaccine efficacy</p>
                  <p>• Higher cancer risk</p>
                  <p>• Autoimmune disease development</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Immunosenescence: Biomarkers & Interventions",
      subtitle: "Assessing and Modulating Immune Age",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Key Biomarkers</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Comprehensive Immune Panels (Flow Cytometry):</strong></p>
                  <p>• Lymphocyte Subsets: Naïve (CD45RA+CCR7+)</p>
                  <p>• Memory (Central, Effector, TEMRA)</p>
                  <p>• Senescent markers (CD28-, CD57+, KLRG1+)</p>
                  <p>• CD4/CD8 Ratio, Regulatory T cells (Tregs)</p>
                  <p>• NK Cell count & function</p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Inflammatory Markers</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p>• hs-CRP, IL-6, TNF-α</p>
                  <p>• DHEA-S (declines with age)</p>
                  <p>• Low levels associated with immune senescence</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Interventions</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Thymosin Alpha 1 (TA1):</strong></p>
                  <p>1.6 mg SC twice weekly or 450-750 mcg SC daily</p>
                  <p>Enhances T-cell & NK cell function, thymopoiesis</p>
                  
                  <p><strong>DHEA Replacement:</strong></p>
                  <p>Women: 150-250 µg/dL target; Men: 300-500 µg/dL</p>
                  <p>Start 5-25mg (W), 10-50mg (M) daily</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Clinical Pearl</h3>
                <p className="text-sm text-yellow-800">
                  Address the gut-immune axis. Dysbiosis contributes significantly to inflammaging.
                  Focus on modulation, not just "boosting" immunity.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pillar 4: Mitochondrial Function & Biogenesis",
      subtitle: "Powering Cellular Vitality: The Mitochondrial Nexus",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Mitochondrial Roles</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Primary:</strong> ATP production via oxidative phosphorylation</p>
                  <p><strong>Also:</strong> Redox signaling, Ca<sup>2+</sup> homeostasis</p>
                  <p>Apoptosis regulation, biosynthesis (heme, steroids)</p>
                </div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Dysfunction in Aging</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>• Impaired ATP production</p>
                  <p>• Increased ROS generation</p>
                  <p>• Altered dynamics (fusion/fission)</p>
                  <p>• Reduced mitophagy (damaged mitochondria clearance)</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Mitochondrial Health Spectrum</h4>
              <div className="flex justify-center">
                <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                    .healthy-mito { fill: #87ceeb; stroke: #4682b4; stroke-width: 2; }
                    .damaged-mito { fill: #ffc0cb; stroke: #dc143c; stroke-width: 2; stroke-dasharray: 5,3; }
                    .cristae { stroke: #ffa500; stroke-width: 2.5; fill: none; }
                    .damaged-cristae { stroke: #8b0000; stroke-width: 2; fill: none; stroke-dasharray: 3,2; }
                  `}</style>
                  
                  <rect x="5" y="5" width="390" height="190" fill="#fffacd" stroke="#e2e8f0" rx="10"/>
                  
                  {/* Healthy Mitochondrion */}
                  <g transform="translate(40, 40)">
                    <ellipse cx="70" cy="50" rx="60" ry="30" className="healthy-mito"/>
                    <path d="M 20 50 Q 40 40, 70 50 T 120 50" className="cristae"/>
                    <path d="M 25 50 Q 45 60, 70 50 T 115 50" className="cristae"/>
                    <text x="70" y="110" className="label" textAnchor="middle">Healthy Mitochondrion</text>
                    <text x="70" y="55" className="label" fontWeight="bold" fill="#006400" textAnchor="middle" fontSize="16">ATP↑</text>
                    <text x="70" y="130" className="label" fill="#006400" textAnchor="middle">ROS↓</text>
                  </g>
                  
                  {/* Arrow */}
                  <line x1="180" y1="90" x2="220" y2="90" stroke="#374151" strokeWidth="2.5" markerEnd="url(#arrowheadMito)"/>
                  <text x="200" y="80" className="label" textAnchor="middle">Aging/</text>
                  <text x="200" y="100" className="label" textAnchor="middle">Damage</text>
                  
                  {/* Damaged Mitochondrion */}
                  <g transform="translate(230, 40)">
                    <ellipse cx="70" cy="50" rx="60" ry="30" className="damaged-mito"/>
                    <path d="M 20 50 Q 40 45, 70 50 T 120 50" className="damaged-cristae"/>
                    <text x="70" y="110" className="label" textAnchor="middle">Dysfunctional</text>
                    <text x="70" y="55" className="label" fontWeight="bold" fill="#dc143c" textAnchor="middle" fontSize="16">ROS↑</text>
                    <text x="70" y="130" className="label" fill="#dc143c" textAnchor="middle">ATP↓</text>
                  </g>
                  
                  <defs>
                    <marker id="arrowheadMito" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#374151"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Therapeutic Goals</h4>
            <p className="text-sm text-green-800">
              Enhance function, promote biogenesis (new mitochondria), support quality control mechanisms
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Mitochondrial Health: Biomarkers & Interventions",
      subtitle: "Assessing and Enhancing Cellular Energy Production",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Key Biomarkers</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Organic Acid Test (OAT - Urine):</strong></p>
                  <p>• Krebs Cycle Intermediates (↑succinate, fumarate)</p>
                  <p>• Fatty Acid Oxidation Markers (↑adipate, suberate)</p>
                  <p>• Lactate/Pyruvate ratio (shift to anaerobic metabolism)</p>
                  
                  <p><strong>Coenzyme Q10:</strong> Total CoQ10, Ubiquinol (active form)</p>
                  <p><strong>Carnitine Profile:</strong> Free & acylcarnitines</p>
                  <p><strong>NAD+ & Metabolites:</strong> NAD+/NADH ratio (specialty labs)</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">NAD+ Precursors</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Nicotinamide Mononucleotide (NMN):</strong> 250-1000 mg/day</p>
                  <p><strong>Nicotinamide Riboside (NR):</strong> 250-1000 mg/day</p>
                  <p>Crucial for electron transport chain & Sirtuins</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Targeted Interventions</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>Coenzyme Q10 (Ubiquinol):</strong> 100-200 mg/day</p>
                  <p><strong>MitoQ:</strong> Mitochondria-targeted CoQ10 analog, 10 mg/day</p>
                  <p><strong>PQQ:</strong> Stimulates mitochondrial biogenesis, 10-20 mg/day</p>
                  <p><strong>L-Carnitine/ALCAR:</strong> Support fatty acid transport</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Lifestyle Factors</h3>
                <div className="space-y-2 text-sm text-yellow-800">
                  <p>• Exercise (especially endurance training)</p>
                  <p>• Caloric Restriction/Intermittent Fasting</p>
                  <p>• Heat/cold exposure (hormetic stress)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Key Signaling Pathways in Longevity: An Overview",
      subtitle: "The Cellular Symphony: Orchestrating Growth, Repair, and Lifespan",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <p className="text-sm text-blue-800 mb-4">
              Aging is profoundly influenced by a network of interconnected signaling pathways that sense and respond to environmental cues (nutrients, stress).
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">Insulin/IGF-1 Signaling (IIS)</h4>
                <p className="text-sm text-blue-800">Promotes growth, anabolism. Reduced activity often pro-longevity.</p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-semibold text-red-900 mb-2">mTOR Pathway</h4>
                <p className="text-sm text-red-800">Central controller of cell growth, proliferation. Inhibition often pro-longevity.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-green-900 mb-2">AMPK Pathway</h4>
                <p className="text-sm text-green-800">Cellular energy sensor. Promotes catabolism, stress resistance. Activation often pro-longevity.</p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                <h4 className="font-semibold text-yellow-900 mb-2">Sirtuins (NAD+-dependent)</h4>
                <p className="text-sm text-yellow-800">Regulate metabolism, stress resistance, DNA repair. Activation often pro-longevity.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Pathway Network</h4>
            <div className="flex justify-center">
              <svg width="450" height="300" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .label { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                  .iis { fill: #bfdbfe; stroke: #3b82f6; }
                  .mtor { fill: #fca5a5; stroke: #dc2626; }
                  .ampk { fill: #86efac; stroke: #16a34a; }
                  .sirtuins { fill: #fef3c7; stroke: #f59e0b; }
                  .cell-fate { fill: #ffffff; stroke: #6b46c1; stroke-width: 2; }
                `}</style>
                
                <rect x="5" y="5" width="440" height="290" fill="#f3f4f6" stroke="#e5e7eb" rx="10"/>
                
                {/* Central Cell Response */}
                <ellipse cx="225" cy="150" rx="70" ry="45" className="cell-fate"/>
                <text x="225" y="145" className="label" textAnchor="middle">Cellular Fate</text>
                <text x="225" y="165" className="label" textAnchor="middle">(Healthspan/Longevity)</text>
                
                {/* Pathways */}
                <rect x="30" y="30" width="120" height="50" className="iis" rx="10"/>
                <text x="90" y="50" className="label" textAnchor="middle">Insulin/IGF-1</text>
                <text x="90" y="65" className="label" textAnchor="middle">(IIS)</text>
                <line x1="100" y1="80" x2="180" y2="115" stroke="#3b82f6" strokeWidth="2" markerEnd="url(#arrowSig)"/>
                
                <rect x="300" y="30" width="120" height="50" className="mtor" rx="10"/>
                <text x="360" y="57" className="label" textAnchor="middle">mTOR</text>
                <line x1="350" y1="80" x2="270" y2="115" stroke="#dc2626" strokeWidth="2" markerEnd="url(#arrowSig)"/>
                
                <rect x="30" y="220" width="120" height="50" className="ampk" rx="10"/>
                <text x="90" y="245" className="label" textAnchor="middle">AMPK</text>
                <line x1="100" y1="220" x2="180" y2="185" stroke="#16a34a" strokeWidth="2" markerEnd="url(#arrowSig)"/>
                
                <rect x="300" y="220" width="120" height="50" className="sirtuins" rx="10"/>
                <text x="360" y="238" className="label" textAnchor="middle">Sirtuins</text>
                <text x="360" y="253" className="label" textAnchor="middle">(NAD+)</text>
                <line x1="350" y1="220" x2="270" y2="185" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowSig)"/>
                
                {/* Pathway interactions */}
                <path d="M 150 55 Q 225 20 300 55" stroke="#8b5cf6" strokeWidth="1.5" fill="none" markerEnd="url(#arrowSig)"/>
                <text x="225" y="30" className="label" fill="#8b5cf6" textAnchor="middle">+</text>
                
                <path d="M 150 245 Q 225 280 300 245" stroke="#7c2d12" strokeWidth="1.5" fill="none" markerEnd="url(#tArrowSig)"/>
                <text x="225" y="285" className="label" fill="#7c2d12" textAnchor="middle">-</text>
                
                <defs>
                  <marker id="arrowSig" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6"/>
                  </marker>
                  <marker id="tArrowSig" markerWidth="8" markerHeight="8" refX="0" refY="4" orient="auto">
                    <line x1="0" y1="0" x2="0" y2="8" stroke="#7c2d12" strokeWidth="1.5"/>
                    <line x1="-3" y1="0" x2="3" y2="0" stroke="#7c2d12" strokeWidth="1.5"/>
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Nutrient Sensing: Insulin/IGF-1 (IIS) & mTOR Pathways",
      subtitle: "Modulating Growth Signals for Longevity",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Insulin/IGF-1 Signaling (IIS)</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Role:</strong> Regulates growth, metabolism, nutrient storage</p>
                  <p>Reduced IIS robustly extends lifespan in model organisms</p>
                  <p><strong>Human Nuance:</strong> Optimal "sweet spot" is key</p>
                  <p>Chronically elevated insulin is detrimental</p>
                  <p>IGF-1 levels in lower-normal range may be beneficial</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Key Biomarkers</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Fasting Insulin (Optimal less than 5 µIU/mL)</p>
                  <p>• Fasting Glucose (Optimal less than 90 mg/dL)</p>
                  <p>• HbA1c (Optimal less than 5.5%, ideally 5.0-5.2%)</p>
                  <p>• HOMA-IR, IGF-1 (age & sex-specific ranges)</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">mTOR Pathway</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p><strong>Role:</strong> Senses nutrients and growth factors</p>
                  <p>Controls cell growth, proliferation, protein synthesis</p>
                  <p><strong>mTORC1 inhibition</strong> (e.g., rapamycin) extends lifespan</p>
                  <p>Works via autophagy induction and reduced protein synthesis</p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Interventions</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>IIS:</strong> Caloric Restriction, Low Glycemic Load Diet, Exercise, Metformin</p>
                  <p><strong>mTOR:</strong> Intermittent Fasting, Protein restriction, Rapamycin</p>
                  <p>Rapamycin: 5-6mg weekly (low, intermittent doses)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Nutrient Sensing: AMPK & Sirtuins/NAD+ Pathways",
      subtitle: "Activating Cellular Guardians of Energy and Stress Resistance",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">AMPK (AMP-activated Protein Kinase)</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Role:</strong> Cellular energy sensor (activated by low energy)</p>
                  <p><strong>Effects:</strong> Promotes catabolism, inhibits anabolism</p>
                  <p>Enhances mitochondrial biogenesis, induces autophagy</p>
                  <p>Improves stress resistance - mimics CR effects</p>
                  <p><strong>Activation is generally pro-longevity</strong></p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">AMPK Interventions</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• Exercise, Caloric Restriction/Fasting</p>
                  <p>• Metformin: 500-2000 mg/day</p>
                  <p>• Berberine</p>
                  <p>Improved metabolic health markers indicate activation</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Sirtuins (SIRT1-7) & NAD+</h3>
                <div className="space-y-2 text-sm text-yellow-800">
                  <p><strong>Role:</strong> NAD+-dependent protein deacetylases</p>
                  <p>Key regulators of gene expression, metabolism</p>
                  <p>Control stress resistance, DNA repair, mitochondrial function</p>
                  <p><strong>NAD+ is essential</strong> for Sirtuin activity</p>
                  <p>NAD+ levels decline with age, impairing function</p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Sirtuin Interventions</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>Boost NAD+:</strong> NMN (250-1000 mg/day), NR (250-1000 mg/day)</p>
                  <p><strong>Sirtuin Activators:</strong> Resveratrol (150-500mg/day), Pterostilbene</p>
                  <p><strong>Lifestyle:</strong> Caloric Restriction, Exercise</p>
                  <p>NAD+/NADH ratio monitoring (specialty labs)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Integrating Other Key Areas for Comprehensive Longevity",
      subtitle: "A Holistic View: Interconnected Systems",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Fatty Acid Metabolism & Membrane Health</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Importance:</strong> Cellular integrity, communication, inflammation modulation</p>
                  <p><strong>Key Test:</strong> RBC Fatty Acid Analysis (Omega-3 Index greater than 8% target)</p>
                  <p><strong>Intervention:</strong> Diet (↑Omega-3s), Omega-3 supplements (EPA/DHA, 2-4g/day)</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Neuroplasticity & Neurotrophic Factors</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Importance:</strong> Cognitive longevity, brain health</p>
                  <p><strong>Key Test:</strong> Serum BDNF, formal cognitive assessments</p>
                  <p><strong>Intervention:</strong> Exercise, cognitive engagement, stress reduction, Lion's Mane, Alpha-GPC</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Hormonal & Neuroendocrine Regulation</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>Importance:</strong> Master regulators declining with age</p>
                  <p><strong>Key Tests:</strong> DHEA-S, Cortisol (diurnal), full Thyroid, Sex Hormones, IGF-1</p>
                  <p><strong>Intervention:</strong> Bioidentical Hormone Replacement, GH Peptides (CJC-1295/Ipamorelin)</p>
                </div>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Detoxification & Biotransformation</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p><strong>Importance:</strong> Managing toxic load; impairment accelerates aging</p>
                  <p><strong>Key Tests:</strong> LFTs, Glutathione status, Genetic SNPs (CYPs, GSTs, MTHFR)</p>
                  <p><strong>Intervention:</strong> Reduce exposure, nutrient cofactors, Nrf2 activators (Sulforaphane, Curcumin)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Clinician's Role: Integrating Complex Data for Actionable Plans",
      subtitle: "From Data to Personalized Strategies",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Key Responsibilities</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• <strong>Synthesize Multi-faceted Data:</strong> Move beyond single biomarker interpretation</p>
                  <p>• <strong>Systems-biology understanding</strong> of patient's unique physiology</p>
                  <p>• <strong>Personalized Interventions</strong> based on comprehensive assessment</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Clinical Integration</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• Comprehensive lab testing</p>
                  <p>• Genetic predispositions (nutrigenomics, pharmacogenomics)</p>
                  <p>• Clinical presentation and patient goals</p>
                  <p>• Prioritize foundational imbalances first</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Implementation Process</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p>• <strong>Address foundational imbalances:</strong> gut health, inflammation, insulin resistance</p>
                  <p>• <strong>Monitor & Follow-Up:</strong> Essential for assessing efficacy</p>
                  <p>• <strong>Adjust protocols:</strong> Iterative process based on response</p>
                  <p>• <strong>Track progress</strong> over time with repeat testing</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Patient Partnership</h3>
                <div className="space-y-2 text-sm text-yellow-800">
                  <p>• <strong>Patient Education:</strong> Explain results and rationale</p>
                  <p>• <strong>Foster adherence</strong> through understanding</p>
                  <p>• <strong>Shared decision-making</strong> for optimal outcomes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Future Directions in Longevity Medicine",
      subtitle: "The Evolving Frontier of Health Optimization",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Emerging Advances</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• <strong>AI-Driven Analytics & Multi-Omics:</strong> Integrating genomics, proteomics, metabolomics</p>
                  <p>• <strong>Highly individualized predictive models</strong> of aging</p>
                  <p>• <strong>Precise intervention strategies</strong> based on comprehensive data</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Advanced Biomarkers</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• More accurate "biological age" clocks</p>
                  <p>• Epigenetic, proteomic, metabolomic markers</p>
                  <p>• Accessible and clinically actionable assessments</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Novel Therapeutics</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p>• More targeted and potent senolytics</p>
                  <p>• Organelle-specific therapies</p>
                  <p>• Next-generation mitochondria-targeted antioxidants</p>
                  <p>• Gene editing and regenerative approaches</p>
                </div>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Systems Understanding</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p>• Greater insights into pathway interconnectedness</p>
                  <p>• Understanding of physiological system interactions</p>
                  <p>• <strong>Clinician's Imperative:</strong> Continuous learning and adaptation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Conclusion & Key Takeaways",
      subtitle: "Embracing Proactive Health for Extended Vitality",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <h3 className="text-xl font-bold text-blue-900 mb-4">Summary</h3>
            <div className="space-y-3 text-sm text-blue-800">
              <p>• Longevity medicine represents a paradigm shift towards proactive, personalized health optimization</p>
              <p>• Functional and advanced lab testing provides crucial insights into underlying physiological imbalances</p>
              <p>• Key intervention pillars include redox balance, autophagy/senescence, immune function, and mitochondrial health</p>
              <p>• Systems-based approach integrating diverse data points is essential for effective strategies</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Clinical Evolution</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>• The clinician's role is evolving to guide patients on evidence-based journeys</p>
                  <p>• Focus on optimized health and extended vitality</p>
                  <p>• Integration of advanced testing with clinical expertise</p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Dynamic Field</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p>• Exciting advancements continually shaping our abilities</p>
                  <p>• Promoting healthy aging through scientific innovation</p>
                  <p>• Continuous learning and adaptation required</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Implementation Checklist</h4>
              <div className="space-y-2 text-sm text-gray-800">
                <div className="space-y-1">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4"/>
                    <span>Establish functional testing protocols</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4"/>
                    <span>Develop biomarker interpretation guidelines</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4"/>
                    <span>Create intervention decision trees</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4"/>
                    <span>Implement monitoring and follow-up systems</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4"/>
                    <span>Establish patient education programs</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Advanced Laboratory Testing Through the SHIFT Framework",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">Functional Lab Testing Mapped to Core Aging Pathways</h3>
            <p className="text-sm text-gray-700">How advanced biomarkers guide precision interventions across SHIFT domains</p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-md font-medium text-amber-800">Sensing & Metabolic Assessment</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Tests:</strong> Comprehensive metabolic panel, insulin/glucose dynamics, HOMA-IR, leptin/adiponectin ratio, continuous glucose monitoring</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Advanced Markers:</strong> HbA1c, fructosamine, 1,5-anhydroglucitol, insulin antibodies, C-peptide, proinsulin</p>
              <p className="text-xs text-gray-700"><strong>Clinical Application:</strong> Metabolic flexibility assessment, insulin resistance staging, nutrient sensing optimization</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-md font-medium text-green-800">Homeostatic Defense & Detoxification</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Tests:</strong> Comprehensive liver function, phase I/II detox pathways, glutathione status, oxidative stress markers</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Advanced Markers:</strong> 8-OHdG, malondialdehyde, SOD, catalase, GPx, CoQ10, NAD+/NADH ratio</p>
              <p className="text-xs text-gray-700"><strong>Clinical Application:</strong> Cellular resilience assessment, antioxidant capacity optimization, detoxification support</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-md font-medium text-red-800">Inflammation & Immune Assessment</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Tests:</strong> High-sensitivity CRP, comprehensive cytokine panel, omega-3 index, food sensitivity testing</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Advanced Markers:</strong> IL-6, TNF-α, IL-1β, NF-κB activity, complement levels, immunoglobulin subclasses</p>
              <p className="text-xs text-gray-700"><strong>Clinical Application:</strong> Inflammatory burden quantification, immune system optimization, targeted anti-inflammatory protocols</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-md font-medium text-purple-800">Fibrosis & Structural Assessment</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Tests:</strong> Collagen synthesis markers, elastography, GDF-15, TGF-β levels, matrix metalloproteinases</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Advanced Markers:</strong> Hydroxyproline, procollagen peptides, fibronectin, hyaluronic acid, TIMP levels</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Tissue integrity assessment, fibrosis risk evaluation, regenerative therapy monitoring</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-md font-medium text-blue-800">Timekeeping & Chronobiology Assessment</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Tests:</strong> Cortisol awakening response, melatonin rhythm, biological age clocks, telomere length analysis</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Advanced Markers:</strong> Epigenetic age clocks, DHEA-S, cortisol variability, circadian gene expression</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Biological age assessment, circadian optimization, longevity biomarker tracking</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-md font-medium text-gray-800 mb-2">Integrated Testing Strategy</h3>
            <p className="text-xs text-gray-700">Advanced laboratory testing provides quantitative assessment across all SHIFT domains, enabling precision medicine approaches. Sequential testing protocols guide targeted interventions, with biomarker panels customized to individual SHIFT domain dysfunction patterns. Regular monitoring tracks intervention efficacy and guides protocol optimization for sustained health optimization.</p>
          </div>
        </div>
      )
    },
    {
      title: "Q&A and References",
      subtitle: "Discussion and Further Reading",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-blue-900 mb-4">Questions & Discussion</h3>
            <p className="text-sm text-blue-800 mb-4">
              We welcome your questions about functional and advanced lab testing implementation, 
              biomarker interpretation strategies, and integration with existing clinical protocols.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Common Questions</h4>
              <div className="space-y-3 text-sm text-gray-800">
                <div>
                  <p className="font-medium">Q: How do I prioritize interventions with multiple abnormal biomarkers?</p>
                  <p>A: Address foundational imbalances first (gut health, inflammation, insulin resistance) before targeting specific pathways.</p>
                </div>
                <div>
                  <p className="font-medium">Q: What's the optimal testing frequency for monitoring?</p>
                  <p>A: Initial comprehensive panel, then targeted follow-up every 3-6 months based on intervention response.</p>
                </div>
                <div>
                  <p className="font-medium">Q: How do I integrate this with conventional medicine?</p>
                  <p>A: Use functional testing to complement standard care, focusing on optimization rather than replacement.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Key References</h4>
              <div className="space-y-2 text-xs text-gray-700">
                <p>1. López-Otín C, et al. The hallmarks of aging. Cell. 2013;153(6):1194-1217.</p>
                <p>2. Kennedy BK, et al. Geroscience: linking aging to chronic disease. Cell. 2014;159(4):709-713.</p>
                <p>3. Sebastiani P, et al. Biomarker signatures of aging. Aging Cell. 2017;16(2):329-338.</p>
                <p>4. Harman D. Aging: a theory based on free radical and radiation chemistry. J Gerontol. 1956;11(3):298-300.</p>
                <p>5. Levine ME, et al. An epigenetic biomarker of aging for lifespan and healthspan. Aging. 2018;10(4):573-591.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg text-center">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Thank You</h4>
            <p className="text-sm text-gray-700">
              For additional resources and continued education in functional medicine and longevity protocols, 
              visit our mentorship platform and explore our comprehensive clinical implementation guides.
            </p>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (event.key === "ArrowRight" && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSlide, slides.length]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
          <p className="text-gray-600">Please sign in to access this lecture.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/mentorship" className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Mentorship
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <span className="text-gray-900 text-sm font-medium">
                  Functional and Advanced Lab Testing
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Functional Advanced Lab Testing" fileName="FunctionalAdvancedLabTesting" />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                SHIFT Framework
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Functional Advanced Lab Testing
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive laboratory testing strategies for longevity medicine
            </p>
          </div>
        </div>

        {/* Slide Counter */}
        <div className="text-center mb-6">
          <span className="text-sm text-gray-500">
            Slide {currentSlide + 1} of {slides.length}
          </span>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          {/* Slide Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {slides[currentSlide].title}
                </h1>
                <p className="text-gray-600 mt-1">{slides[currentSlide].subtitle}</p>
              </div>
            </div>
          </div>

          {/* Slide Content */}
          <div className="p-6">
            {slides[currentSlide].content}
          </div>

          {/* Navigation */}
          <div className="p-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={currentSlide === 0}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                <span>Previous</span>
              </button>

              <div className="flex space-x-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full ${
                      index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
                disabled={currentSlide === slides.length - 1}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded Audio Lecture */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Audio Lecture</h2>
              <p className="text-gray-600 mt-1">Listen to the comprehensive audio presentation</p>
            </div>
            <div className="bg-blue-50 px-4 py-2 rounded-lg">
              <span className="text-blue-800 text-sm font-medium">Enhanced Learning</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <iframe
              src="https://share.descript.com/embed/rfxtRMprgll"
              width="100%"
              height="360"
              frameBorder="0"
              allowFullScreen
              className="rounded-lg shadow-sm"
              title="Functional and Advanced Lab Testing Audio Lecture"
            ></iframe>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <p className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Audio lecture covers all 22 slides with detailed explanations and clinical insights
            </p>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>
    </div>
  );
}